import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Eye, EyeOff, Copy, Lock, Shield, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import './ShareView.css';

const ShareView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = window.location.pathname;
  const isKubeconfigShare = location.includes('/share/kubeconfig/');
  const isVpnShare = location.includes('/share/vpn/');
  const resourceType = isVpnShare ? 'vpn' : (isKubeconfigShare ? 'kubeconfig' : 'credential');
  
  const [password, setPassword] = useState('');
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [credential, setCredential] = useState<any>(null);
  const [kubeconfig, setKubeconfig] = useState<any>(null);
  const [vpn, setVpn] = useState<any>(null);
  const [shareInfo, setShareInfo] = useState<any>(null);

  useEffect(() => {
    if (token) {
      fetchSharedResource();
    }
  }, [token, password, resourceType]);

  const fetchSharedResource = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (password) {
        params.append('password', password);
      }
      let endpoint;
      if (resourceType === 'kubeconfig') {
        endpoint = `/shares/kubeconfig/share/${token}?${params.toString()}`;
      } else if (resourceType === 'vpn') {
        endpoint = `/shares/vpn/share/${token}?${params.toString()}`;
      } else {
        endpoint = `/shares/public/${token}?${params.toString()}`;
      }
      const response = await api.get(endpoint);
      
      if (resourceType === 'kubeconfig') {
        setKubeconfig(response.data.kubeconfig);
      } else if (resourceType === 'vpn') {
        setVpn(response.data.vpn);
      } else {
        setCredential(response.data.credential);
      }
      setShareInfo(response.data.share);
      setRequiresPassword(false);
    } catch (err: any) {
      if (err.response?.status === 401 && err.response?.data?.requiresPassword) {
        setRequiresPassword(true);
        const resourceLabel = resourceType === 'kubeconfig' ? 'kubeconfig' : (resourceType === 'vpn' ? 'VPN' : 'credential');
        setError(`Password required to access this shared ${resourceLabel}`);
      } else {
        const resourceLabel = resourceType === 'kubeconfig' ? 'kubeconfig' : (resourceType === 'vpn' ? 'VPN' : 'credential');
        setError(err.response?.data?.error || `Failed to load shared ${resourceLabel}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
      setTimeout(async () => {
        await navigator.clipboard.writeText('');
      }, 30000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="share-view-container">
        <div className="share-view-card">
          <div className="loading">Loading shared credential...</div>
        </div>
      </div>
    );
  }

  if (error && !requiresPassword) {
    const isExpired = error.includes('expired');
    const isUsed = error.includes('already been used');
    
    return (
      <div className="share-view-container">
        <div className="share-view-card error-card">
          <AlertCircle size={48} className="error-icon" />
          <h2>Access Denied</h2>
          <p>{error}</p>
          <p className="error-details">
            {isExpired && `This share link has expired. Please request a new link from the ${resourceType === 'kubeconfig' ? 'kubeconfig' : (resourceType === 'vpn' ? 'VPN' : 'credential')} owner.`}
            {isUsed && 'This is a one-time use link and has already been accessed. Please request a new link if you need access again.'}
            {!isExpired && !isUsed && 'This link may be invalid or you may not have permission to access it.'}
          </p>
        </div>
      </div>
    );
  }

  if (requiresPassword) {
    return (
      <div className="share-view-container">
        <div className="share-view-card">
          <div className="password-prompt">
            <Lock size={48} className="lock-icon" />
            <h2>Password Required</h2>
            <p>This shared {resourceType === 'kubeconfig' ? 'kubeconfig' : (resourceType === 'vpn' ? 'VPN' : 'credential')} is password protected.</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                fetchSharedResource();
              }}
              className="password-form"
            >
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                autoFocus
              />
              {error && <div className="error-message">{error}</div>}
              <button type="submit" className="btn-primary">
                Access {resourceType === 'kubeconfig' ? 'Kubeconfig' : (resourceType === 'vpn' ? 'VPN' : 'Credential')}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const resource = credential || kubeconfig || vpn;
  if (!resource) return null;

  return (
    <div className="share-view-container">
      <div className="share-view-card">
        <div className="share-header">
          <Shield size={32} className="shield-icon" />
          <h1>Shared {resourceType === 'kubeconfig' ? 'Kubeconfig' : (resourceType === 'vpn' ? 'VPN' : 'Credential')}</h1>
          <p className="share-subtitle">
            Shared by <strong>{resource.createdBy.username}</strong>
          </p>
        </div>

        {resourceType === 'kubeconfig' ? (
          <div className="credential-content">
            <div className="credential-header">
              <h2>{kubeconfig.name}</h2>
              {kubeconfig.description && <p className="description">{kubeconfig.description}</p>}
              <div className="badges">
                {kubeconfig.clusterName && <span className="badge">Cluster: {kubeconfig.clusterName}</span>}
                {kubeconfig.contextName && <span className="badge">Context: {kubeconfig.contextName}</span>}
              </div>
            </div>

            <div className="credential-data">
              <div className="data-field">
                <label>Kubeconfig Content</label>
                <div className="field-value">
                  <textarea
                    readOnly
                    value={kubeconfig.data.content}
                    rows={15}
                    style={{ fontFamily: 'monospace', fontSize: '12px', width: '100%', padding: '10px' }}
                  />
                  <button
                    className="copy-btn"
                    onClick={() => handleCopy(kubeconfig.data.content, 'Kubeconfig')}
                    style={{ marginTop: '10px' }}
                  >
                    <Copy size={16} /> Copy Content
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : resourceType === 'vpn' ? (
          <div className="credential-content">
            <div className="credential-header">
              <h2>{vpn.name}</h2>
              {vpn.description && <p className="description">{vpn.description}</p>}
              <div className="badges">
                {vpn.serverAddress && <span className="badge">Server: {vpn.serverAddress}</span>}
                {vpn.protocol && <span className="badge">Protocol: {vpn.protocol}</span>}
              </div>
            </div>

            <div className="credential-data">
              <div className="data-field">
                <label>VPN Config Content</label>
                <div className="field-value">
                  <textarea
                    readOnly
                    value={vpn.data.content}
                    rows={15}
                    style={{ fontFamily: 'monospace', fontSize: '12px', width: '100%', padding: '10px' }}
                  />
                  <button
                    className="copy-btn"
                    onClick={() => handleCopy(vpn.data.content, 'VPN')}
                    style={{ marginTop: '10px' }}
                  >
                    <Copy size={16} /> Copy Content
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="credential-content">
            <div className="credential-header">
              <h2>{credential.name}</h2>
              {credential.description && <p className="description">{credential.description}</p>}
              <div className="badges">
                <span className="badge">{credential.category.replace(/_/g, ' ')}</span>
                <span className="badge">{credential.type.replace(/_/g, ' ')}</span>
              </div>
            </div>

          <div className="credential-data">
            {credential.data.username && (
              <div className="data-field">
                <label>Username</label>
                <div className="field-value">
                  <span>{credential.data.username}</span>
                  <button
                    className="copy-btn"
                    onClick={() => handleCopy(credential.data.username, 'Username')}
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>
            )}

            {credential.data.password && (
              <div className="data-field">
                <label>Password</label>
                <div className="field-value">
                  <span className="password-field">
                    {revealedPassword ? credential.data.password : '••••••••'}
                  </span>
                  <div className="field-actions">
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(credential.data.password, 'Password')}
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      className="copy-btn"
                      onClick={() => setRevealedPassword(!revealedPassword)}
                    >
                      {revealedPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {credential.data.host && (
              <div className="data-field">
                <label>Host</label>
                <div className="field-value">
                  <span>{credential.data.host}</span>
                  <button className="copy-btn" onClick={() => handleCopy(credential.data.host, 'Host')}>
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            )}

            {credential.data.port && (
              <div className="data-field">
                <label>Port</label>
                <div className="field-value">
                  <span>{credential.data.port}</span>
                  <button className="copy-btn" onClick={() => handleCopy(String(credential.data.port), 'Port')}>
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            )}

            {credential.data.token && (
              <div className="data-field">
                <label>API Token</label>
                <div className="field-value">
                  <span className="password-field">
                    {revealedPassword ? credential.data.token : '••••••••'}
                  </span>
                  <div className="field-actions">
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(credential.data.token, 'Token')}
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      className="copy-btn"
                      onClick={() => setRevealedPassword(!revealedPassword)}
                    >
                      {revealedPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {shareInfo && (
          <div className="share-info">
            <p>
              <strong>One-time use:</strong> {shareInfo.isOneTimeUse ? 'Yes' : 'No'}
            </p>
            <p>
              <strong>Expires:</strong>{' '}
              {shareInfo.expiresAt ? new Date(shareInfo.expiresAt).toLocaleString() : 'Never'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareView;

